import { Box, Typography, Button, Stack, Link } from "@mui/material";
import PageSection from "../sections/PageSection";
// import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import StorefrontIcon from "@mui/icons-material/Storefront";
import type { SvgIconComponent } from "@mui/icons-material";
import { radius, sectionColors } from "../styles/tokens";
import { useSiteTheme } from "../theme/ThemeContext";

interface NavLinkItem {
  label: string;
  href: string;
}

interface SocialNavLinkItem extends NavLinkItem {
  icon: SvgIconComponent;
}

export default function FooterSection() {
  const year = new Date().getFullYear();
  const navLinks: NavLinkItem[] = [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ];

  const socialNavLinks: SocialNavLinkItem[] = [
    {
      label: "Etsy",
      href: "https://byzantineartstudio.etsy.com",
      icon: StorefrontIcon,
    },
  ];
  const { palette } = useSiteTheme();

  return (
    <PageSection
      bg={palette.footer}
      sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 5, md: 6 } }}
    >
      <Box
        sx={{
          borderRadius: radius.surface,
          bgcolor: palette.footer,
          color: palette.text,
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={{ xs: 5, md: 8 }}
        >
          <Box
            sx={{
              maxWidth: 360,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                  mb: 1.5,
                }}
              >
                Byzantine Art Studio
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.8,
                  color: "rgba(79, 70, 63, 0.82)",
                }}
              >
                Hand painted icons.
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: { xs: 4, md: 0 },
                fontSize: "0.85rem",
                color: "rgba(79, 70, 63, 0.68)",
              }}
            >
              © {year} Byzantine Art Studio. All rights reserved.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 4, sm: 8, md: 10 }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  mb: 2,
                  letterSpacing: "0.08em",
                  color: "rgba(79, 70, 63, 0.7)",
                }}
              >
                Explore
              </Typography>

              <Stack spacing={1.25}>
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    underline="none"
                    color="inherit"
                    sx={{
                      display: "inline-block",
                      width: "fit-content",
                      fontSize: "0.95rem",
                      letterSpacing: "0.01em",
                      color: "rgba(79, 70, 63, 0.86)",
                      transform: "translateY(0)",
                      transition: "transform 0.22s ease, opacity 0.22s ease",
                      opacity: 0.9,
                      "&:hover": {
                        transform: "translateY(-2px)",
                        opacity: 0.7,
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  mb: 2,
                  letterSpacing: "0.08em",
                  color: "rgba(79, 70, 63, 0.7)",
                }}
              >
                Follow
              </Typography>

              <Stack spacing={1.25}>
                {socialNavLinks.map((link) => {
                  const Icon = link.icon;

                  return (
                    <Link
                      key={link.label}
                      href={link.href}
                      underline="none"
                      color="inherit"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        width: "fit-content",
                        fontSize: "0.95rem",
                        color: "rgba(79, 70, 63, 0.84)",
                        transform: "translateY(0)",
                        transition: "transform 0.22s ease, opacity 0.22s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          opacity: 0.7,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                        }}
                      >
                        <Icon sx={{ fontSize: 16, opacity: 0.75 }} />
                      </Box>
                      {link.label}
                    </Link>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </PageSection>
  );
}
